// Browser adapter for the universal LiveKit data transport. Feature code must
// never call `room.localParticipant.publishData(...)` directly.
//
// WHY IT MATTERS: LiveKit drops any reliable data packet larger than 65535 bytes
// server-side — silently, from the sender's view — AND the oversized forward
// WEDGES each subscriber's data channel (DTLS timeout). On staging that wedge
// killed the BitHuman cloud avatar's audio (its PCM rides the SAME reliable data
// channel in cloud mode) and degraded ALL reliable data (chat, transcript,
// tool-sharing) for every participant. Confirmed: 14 oversized-packet errors in
// 6h, in the exact rooms whose calls failed.
//
// Oversized reliable messages are fragmented into application-level envelopes
// below the smallest observed subscriber limit. A durable/stateful feature
// should still use a compact signal + hydrate path; chunking is the universal
// safety net for messages that cannot be made compact at the call site.

import {
  LIVEKIT_DATA_SAFE_PACKET_BYTES,
  LIVEKIT_DATA_RAW_CHUNK_BYTES,
  LIVEKIT_DATA_WARN_BYTES,
  LIVEKIT_RELIABLE_PACKET_HARD_CAP_BYTES,
  chunkLiveKitDataPayload,
} from './dataTransport';

// Backward-compatible exports for existing diagnostics/tests.
export const RELIABLE_PACKET_HARD_CAP = LIVEKIT_RELIABLE_PACKET_HARD_CAP_BYTES;
export const GUARD_SKIP_THRESHOLD = LIVEKIT_DATA_SAFE_PACKET_BYTES;
export const GUARD_WARN_THRESHOLD = LIVEKIT_DATA_WARN_BYTES;

type PublishDataOptions = {
  reliable?: boolean;
  topic?: string;
  destinationIdentities?: string[];
  [key: string]: unknown;
};

interface PublishCapableRoom {
  localParticipant: {
    publishData: (data: Uint8Array, options?: PublishDataOptions) => Promise<void>;
  };
}

/**
 * Drop-in replacement for `room.localParticipant.publishData(data, options)`.
 * Safe packets publish once. Oversized packets are fragmented and published as
 * a reliable sequence; receivers reconstruct them before application decoding.
 * Returns true only after every packet has been accepted by the local SDK.
 */
export async function publishDataGuarded(
  room: PublishCapableRoom,
  data: Uint8Array,
  options: PublishDataOptions = {},
): Promise<boolean> {
  const size = data.byteLength;
  const topic = options.topic ?? '';
  const reliable = options.reliable !== false;

  // A lossy message cannot be reconstructed: any chunk may be dropped. Refuse
  // it loudly instead of silently changing cursor/presence semantics to
  // reliable or delivering a partial application message.
  if (!reliable && size > GUARD_SKIP_THRESHOLD) {
    throw new Error(
      `Oversized unreliable LiveKit payload cannot be reassembled safely ` +
        `(topic="${topic}", bytes=${size})`,
    );
  }

  if (size > GUARD_WARN_THRESHOLD) {
    const packetCount = Math.ceil(size / LIVEKIT_DATA_RAW_CHUNK_BYTES);
    // eslint-disable-next-line no-console -- production diagnostic: name the offender
    console.warn(
      size > GUARD_SKIP_THRESHOLD
        ? `[DATA-TRANSPORT] topic="${topic}" payload ${size}B exceeds the single-packet ` +
            `budget; publishing ${packetCount} bounded reliable chunks. Prefer a compact ` +
            `signal + hydrate contract for durable state.`
        : `[DATA-TRANSPORT] topic="${topic}" payload ${size}B is approaching the ` +
            `${RELIABLE_PACKET_HARD_CAP}B reliable cap.`,
    );
  }

  // Always hand the SDK the reliability we computed. `publishData` treats a
  // MISSING `reliable` as LOSSY, so forwarding the caller's options unchanged
  // for a one-packet send silently downgrades every caller that relies on this
  // adapter's documented reliable-by-default contract.
  const packets = reliable ? chunkLiveKitDataPayload(data, { topic }) : [data];
  const publishOptions = { ...options, reliable };
  for (const packet of packets) {
    await room.localParticipant.publishData(packet, publishOptions);
  }
  return true;
}
